import {
  Button,
  Card,
  Stack,
  Typography,
  Divider,
  ButtonBase,
  useMediaQuery,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import { NumericFormat } from 'react-number-format';
import { Link, Navigate } from 'react-router-dom';

import Print from '../../assets/icons/Print';
import PaymentsCards from '../../components/PaymentsInfo/PaymentsCards';
import PaymentsTable from '../../components/PaymentsInfo/PaymentsTable';
import StatusAlert from '../../components/styledComponents/StatusAlert';
import colors, { csaaBlack } from '../../constants/colors';
import { StatusType } from '../../constants/enums';
import { usePoliciesQuery } from '../../api/getPolicies';
import { usePaymentsContext } from '../../store/payments';
import { resetPaymentState } from '../../store/paymentsReducer';
import ReviewCardCommonEntry from '../Payment/components/ReviewCardCommonEntry';
import ClearspeedModal from '../../components/ClearspeedModal';
import { isMacOSUser } from '../../utils/deviceDetection';

const PaymentStatusPage = () => {
  const { dispatch, state } = usePaymentsContext();
  const { data: policiesData } = usePoliciesQuery();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('mobile'));

  const userEmail = state.confirmationEmail;
  const policyNumbers = Object.keys(state.selectedPayments).filter(
    (policyNumber) => Number(state.selectedPayments[policyNumber].amount) > 0
  );

  const autoPayBanner = state.autoPayBanner;

  const isSinglePayment = !isEmpty(state.singleSelectedPayment);

  const hasCancelledPolicy = isSinglePayment
    ? (state.singleSelectedPayment.policyStatus
        ?.toLowerCase()
        .includes('cancel') ?? false)
    : policyNumbers.some((policyNumber) => {
        const policy = policiesData?.policies?.find(
          (policy) => policy.policyNumber === policyNumber
        );
        return policy?.policyStatus?.toLowerCase().includes('cancel');
      });

  const isClearspeedEnabled =
    import.meta.env.VITE_CLEARSPEED_ENABLED === 'true';
  const isMacUser = isMacOSUser();
  const shouldShowClearspeed =
    isClearspeedEnabled && !isMacUser && hasCancelledPolicy;

  const [isClearspeedOpen, setIsClearspeedOpen] = useState(false);

  useEffect(() => {
    if (shouldShowClearspeed) {
      setIsClearspeedOpen(true);
    }
  }, [shouldShowClearspeed]);

  const onClearspeedClose = () => {
    setIsClearspeedOpen(false);
  };

  const statusBannerProps = {
    title: 'Your payment was submitted.',
    status: StatusType.SUCCESS,
    className: 'csaa-qm-encrypt',
  };

  const selectedPaymentMethod = state.selectedPaymentMethod!;

  const getTotal = () =>
    policyNumbers.reduce(
      (acc, policyNumber) =>
        acc + Number(state.selectedPayments[policyNumber].amount),
      0
    );

  return (
    <>
      <ClearspeedModal isOpen={isClearspeedOpen} onClose={onClearspeedClose} />
      {!selectedPaymentMethod && <Navigate to="/bills" replace />}
      <Stack
        direction="column"
        data-testid="paymentStatusPage"
        spacing={{ mobile: 2, tablet: 3 }}
        sx={{
          width: 1,
          minHeight: '100%',
          backgroundColor: colors.csaaGray,
          px: { mobile: 2, desktop: 19.5 },
          pb: 3,
          pt: { mobile: 3, desktop: 9 },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: { mobile: 400, tablet: 500 },
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '32px', tablet: '56px' },
          }}
        >
          Payment status
        </Typography>
        <StatusAlert open {...statusBannerProps} />
        {autoPayBanner.show && (
          <StatusAlert
            open
            className="csaa-qm-encrypt"
            text={autoPayBanner.text}
            status={autoPayBanner.status}
            title={autoPayBanner.title}
          />
        )}
        <Card
          component={Stack}
          spacing={3}
          sx={{
            borderRadius: 2.5,
            px: {
              mobile: 2,
              tablet: 3,
              desktop: 5,
            },
            py: {
              mobile: 2,
              tablet: 3,
              desktop: 5,
            },
            height: 'auto',
          }}
        >
          <Stack
            spacing={1}
            divider={<Divider />}
            sx={{
              border: '1px solid',
              borderColor: colors.csaaLightBorder,
              borderRadius: 2,
              p: 3,
            }}
          >
            <ReviewCardCommonEntry
              label="Payment method"
              text={selectedPaymentMethod?.displayPaymentMethod}
              className="csaa-qm-block"
              testId="payment-confirmation-payment-method"
            />
            <ReviewCardCommonEntry
              label={
                selectedPaymentMethod?.analyticsAccountType ===
                'Debit/Credit Card'
                  ? 'Name on card'
                  : 'Name on account'
              }
              text={selectedPaymentMethod?.name}
              className="csaa-qm-block"
              testId="payment-confirmation-name-on-payment-method"
            />
            {userEmail && (
              <ReviewCardCommonEntry
                label="Confirmation email"
                text={userEmail}
                className="csaa-qm-encrypt"
                testId="payment-confirmation-confirmation-email"
              />
            )}
            {isSinglePayment && (
              <ReviewCardCommonEntry
                label={`${state.singleSelectedPayment.policyType} policy`}
                text={state.singleSelectedPayment.policyNumber}
                testId="payment-confirmation-policy-number"
              />
            )}
            <ReviewCardCommonEntry
              label="Total"
              text={
                <NumericFormat
                  value={
                    isSinglePayment
                      ? state.singleSelectedPayment.amount
                      : getTotal()
                  }
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                />
              }
              testId="payment-confirmation-total"
            />
          </Stack>
          {!isSinglePayment && isMobile && (
            <PaymentsCards policyNumbers={policyNumbers} showConfirmation />
          )}
          {!isSinglePayment && !isMobile && (
            <PaymentsTable
              showConfirmation
              rows={policyNumbers.map((policyNumber) => ({
                policyType: state.selectedPayments[policyNumber].policyType,
                policyNumber,
                policyStatus: state.selectedPayments[policyNumber].policyStatus,
                amount: state.selectedPayments[policyNumber].amount!,
                confirmation:
                  state.paymentDetails[policyNumber]?.receiptNumber ?? '-',
              }))}
            />
          )}
          <Stack
            direction="column"
            alignItems="flex-start"
            spacing={{ mobile: 3, tablet: 2 }}
          >
            <ButtonBase
              aria-label="Print Confirmation"
              sx={{
                borderRadius: 6,
                border: `1px solid ${csaaBlack}`,
                py: '6px',
                px: '12px',
                fontFamily: 'Cabin',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
              }}
              disableRipple
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                window.print();
              }}
            >
              <Print sx={{ mr: 0.5 }} />
              Print confirmation
            </ButtonBase>
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontSize: { mobile: 16, tablet: 18 },
                fontWeight: 400,
                lineHeight: '24px',
              }}
            >
              A confirmation will be sent to {userEmail ?? 'your email'}. It may
              take up to 48 hours before your account reflects the payment.
            </Typography>
          </Stack>
          <Stack
            justifyContent="flex-end"
            direction="row"
            sx={{ mt: { mobile: 3, tablet: 5 }, width: 1 }}
          >
            <Button
              component={Link}
              to="/bills"
              variant="contained"
              color="primary"
              data-testid="paymentStatusDone"
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                px: 4,
                py: 0.5,
                displayPrint: 'none',
              }}
              onClick={() => {
                dispatch(resetPaymentState());
              }}
            >
              Done
            </Button>
          </Stack>
          {/* <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: 12,
            lineHeight: '16px',
            textAlign: { mobile: 'center', tablet: 'right' },
            letterSpacing: '0.2px',
            mb: 7,
          }}
        >
          By submitting this payment, you agree to the{' '}
          <Link
            to="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/terms-of-use.html"
            target="_blank"
            sx={{
              fontFamily: 'inherit',
              fontSize: 12,
            }}
          >
            Terms and Conditions
          </Link>
          .
        </Typography> */}
        </Card>
      </Stack>
    </>
  );
};

export default PaymentStatusPage;
